import { cn } from "@/lib/cn";
import { useSession } from "@/components/providers/session-provider";
import { useSidebar } from "@/components/providers/sidebar-provider";
import { useNotifications } from "@/components/providers/notifications-provider";
import { useAdminDashboard } from "@/components/providers/admin-dashboard-provider";
import { ThemeToggle } from "./theme-toggle";
import { TROPHY_ICON } from "./nav-config";
import { InstallTopbarButton } from "@/components/install-prompt";
import { WorkspaceSwitcher } from "@/components/workspace-switcher";
import { Bell, KeyRound, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";

/**
 * Human-readable label for a route, shown on desktop.
 *
 * Exported separately from the hook so the mapping can be checked without mounting the topbar;
 * the first pattern to match wins, so more specific admin routes are listed before `/admin`.
 */
export function routeTitle(pathname: string): string {
  const map: Array<[RegExp, string]> = [
    [/^\/dashboard$/, "Dashboard"],
    [/^\/challenges$/, "Choose a challenge"],
    [/^\/badges/, "Badges"],
    [/^\/exercises/, "Exercises"],
    [/^\/memories/, "Memories"],
    [/^\/kudos/, "Kudos"],
    [/^\/notifications/, "Notifications"],
    [/^\/workspaces/, "Workspaces"],
    [/^\/workspace-admin\/emails/, "Campaign emails"],
    [/^\/workspace-admin/, "Campaign operations"],
    [/^\/integrations/, "Integrations"],
    [/^\/profile/, "Profile"],
    [/^\/admin\/challenges/, "Campaigns"],
    [/^\/admin\/access-requests/, "Access requests"],
    [/^\/admin\/users/, "Portal users"],
    [/^\/admin\/themes/, "Themes"],
    [/^\/admin\/announcements/, "Announcements"],
    [/^\/admin\/whatsapp/, "WhatsApp"],
    [/^\/admin\/memories/, "Memories"],
    [/^\/admin\/feedback/, "Feedback review"],
    [/^\/admin\/reports\/operations/, "Campaign operations"],
    [/^\/admin\/audit/, "Audit log"],
    [/^\/admin\/health/, "Portal health"],
    [/^\/admin$/, "Admin"],
    [/\/completion$/, "Final recap"],
    [/\/tracker$/, "Log activity"],
    [/\/leaderboard$/, "Leaderboard"],
    [/\/teams$/, "Teams"],
    [/\/register$/, "Registration"],
    [/^\/challenges\//, "Challenge"],
  ];
  return map.find(([re]) => re.test(pathname))?.[1] ?? "";
}

function useRouteTitle(): string {
  const { pathname } = useLocation();
  return routeTitle(pathname);
}

export function Topbar() {
  const { me } = useSession();
  const { openDrawer } = useSidebar();
  const { unreadCount } = useNotifications();
  const { pendingAccessRequestCount } = useAdminDashboard();
  const title = useRouteTitle();

  if (me?.authenticated !== true) return null;

  return (
    <header className="glass no-print sticky top-0 z-30 flex h-[var(--topbar-h)] items-center gap-3 border-b border-border px-4 sm:px-6">
      <button
        type="button"
        onClick={openDrawer}
        aria-label="Open navigation"
        className="flex h-10 w-10 items-center justify-center rounded-xl text-text-muted transition-colors hover:bg-surface-muted hover:text-text lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Compact brand on mobile, where the sidebar is hidden. */}
      <Link to="/dashboard" className="flex items-center gap-2 lg:hidden">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--brand-accent)] text-white">
          <TROPHY_ICON className="h-4 w-4" />
        </span>
        <span className="text-sm font-extrabold tracking-tight text-text">
          STEP<span className="text-[var(--brand-accent)]">-</span>TEMBER
        </span>
      </Link>

      {title && (
        <h1 className="hidden text-[15px] font-bold tracking-tight text-text lg:block">
          {title}
        </h1>
      )}

      <WorkspaceSwitcher />

      <div className="ml-auto flex items-center gap-1.5">
        <InstallTopbarButton />
        <ThemeToggle />

        {me.role === "Administrator" && (
          <Link
            to="/admin/access-requests"
            aria-label={
              pendingAccessRequestCount > 0
                ? `Access requests, ${pendingAccessRequestCount} pending`
                : "Access requests"
            }
            className="relative flex h-10 w-10 items-center justify-center rounded-xl text-text-muted transition-colors hover:bg-surface-muted hover:text-text"
          >
            <KeyRound className="h-5 w-5" />
            {pendingAccessRequestCount > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--brand-accent)] px-1 text-[9px] font-bold text-white">
                {pendingAccessRequestCount > 99
                  ? "99+"
                  : pendingAccessRequestCount}
              </span>
            )}
          </Link>
        )}

        <Link
          to="/notifications"
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
          className="relative flex h-10 w-10 items-center justify-center rounded-xl text-text-muted transition-colors hover:bg-surface-muted hover:text-text"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className={cn(
                "absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--brand-accent)] px-1 text-[9px] font-bold text-white",
              )}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Link>
      </div>
    </header>
  );
}
